import { ArrowDownToLine, MapPin, Mail, Phone } from "lucide-react";
import { Section, SectionTitle, Reveal } from "@/components/Section";
import { useContent } from "@/i18n";
import { Shape3D, HandNote } from "@/components/Objects3D";

export const Resume = () => {
  const { c, ui } = useContent();
  const profile = c.profile;
  return (
    <Section id="resume" num="10" chapter={ui.resume.chapter} blue>
      <Shape3D shape="book" size={60} className="absolute left-[4%] top-24 opacity-80 hidden md:block" duration={7} />
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <SectionTitle>
              {ui.resume.titleA} <span className="italic text-cobalt">{ui.resume.titleB}</span> {ui.resume.titleC}
            </SectionTitle>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base leading-relaxed text-cocoa max-w-sm">{ui.resume.intro}</p>
            </Reveal>
            <Reveal delay={0.25}>
              <a
                href={profile.cvUrl}
                download
                data-testid="resume-download-button"
                className="group mt-8 inline-flex items-center gap-3 rounded-full bg-cobalt text-cream px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-bold hover:bg-choco transition-colors duration-400"
              >
                {ui.resume.download}
                <ArrowDownToLine size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
              </a>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 space-y-3 text-sm text-cocoa">
                <p className="flex items-center gap-3"><Mail size={14} className="text-cobalt" /> {profile.email}</p>
                <p className="flex items-center gap-3"><Phone size={14} className="text-cobalt" /> {profile.phone}</p>
                <p className="flex items-center gap-3"><MapPin size={14} className="text-cobalt" /> {profile.location}</p>
              </div>
            </Reveal>
            <Reveal delay={0.35}>
              <HandNote className="mt-8 inline-block -rotate-2">{ui.resume.note}</HandNote>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-8">
          <Reveal delay={0.1}>
            <div className="bg-white border border-line rounded-2xl rotate-1 p-8 md:p-12 shadow-[0_36px_70px_-34px_rgba(51,38,25,0.35)]" data-testid="resume-preview">
              <div className="flex flex-wrap items-start justify-between gap-4 pb-8 border-b border-line">
                <div>
                  <h3 className="font-serif text-3xl md:text-4xl tracking-tight">{profile.name}</h3>
                  <p className="mt-2 text-xs tracking-[0.2em] uppercase text-cobalt font-bold">
                    M.Sc. Psychology & Management
                  </p>
                </div>
                <div className="text-right text-xs text-cocoa space-y-1">
                  <p>{profile.location}</p>
                  <p>{profile.email}</p>
                </div>
              </div>

              <div className="mt-8">
                <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-cobalt">{ui.resume.profile}</p>
                <p className="mt-3 text-sm leading-relaxed text-cocoa">{ui.resume.profileText}</p>
              </div>

              <div className="mt-8 grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-cobalt">{ui.resume.education}</p>
                  <ul className="mt-3 space-y-3">
                    {c.education.slice(0, 3).map((e) => (
                      <li key={e.degree}>
                        <p className="text-sm font-bold">{e.degree}</p>
                        <p className="text-xs text-cocoa">{e.institution} · {e.period}</p>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-cobalt">{ui.resume.experience}</p>
                  <ul className="mt-3 space-y-3">
                    {c.experience.map((e) => (
                      <li key={e.role}>
                        <p className="text-sm font-bold">{e.role}</p>
                        <p className="text-xs text-cocoa">{e.org} · {e.period}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-line">
                <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-cobalt">{ui.resume.coreSkills}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {c.skillGroups.flatMap((g) => g.items).slice(0, 14).map((s) => (
                    <span key={s} className="rounded-full bg-ivory border border-line px-2.5 py-1 text-[11px] text-cocoa">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </Section>
  );
};
